import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { summarize, relativeDays, type DashboardItem } from "../../shared/dashboard";
import { formatMoney } from "../../shared/money";
import { matchesLocally, searchTerms } from "../../shared/search";
import { api } from "../lib/api";
import { CATEGORIES, TYPES, typeFor } from "../../shared/catalog";
import { contractValue, extraSearchText, peopleLine } from "../../shared/contract";
import { SORTS, listDateLine, sortContracts, type SortKey } from "../lib/display";
import type { LocalContract } from "../lib/db";
import { useContracts, useOnline, useSession } from "../lib/session";
import { Badge, EmptyState, PageHeader, Select, Spinner, TextInput, buttonClass, cx, focusRing } from "../components/ui";

export const FILTERS = {
  all: "All",
  active: "Active",
  notice: "Notice due this month",
  "30": "Ending in 30 days",
  "60": "Ending in 31–60 days",
  "90": "Ending in 61–90 days",
  expired: "Expired",
  terminated: "Terminated",
} as const;
type Filter = keyof typeof FILTERS;
/** Display order. Object.entries would put "30", "60" and "90" first, since they look like numbers. */
export const FILTER_ORDER: Filter[] = ["all", "active", "notice", "30", "60", "90", "expired", "terminated"];

function statusBadge(item: DashboardItem<LocalContract>, today: string) {
  if (item.contract.status === "terminated") return <Badge>Terminated</Badge>;
  if (item.termEnd && item.termEnd < today) return <Badge tone="bad">Expired</Badge>;
  if (item.daysToDeadline !== null && item.daysToDeadline >= 0 && item.daysToDeadline <= 30) return <Badge tone="warn">Notice {relativeDays(item.daysToDeadline)}</Badge>;
  return <Badge tone="good">Active</Badge>;
}

export function Contracts() {
  const contracts = useContracts();
  const { today } = useSession();
  const online = useOnline();
  const [params, setParams] = useSearchParams();
  const filter = (params.get("filter") ?? "all") as Filter;
  const category = params.get("category") ?? "";
  const type = params.get("type") ?? "";
  const sort = (params.get("sort") ?? "deadline") as SortKey;
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // A type from another category no longer applies.
    if (key === "category" && type && typeFor(type)?.category !== value) next.delete("type");
    setParams(next, { replace: true });
  };
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [serverHits, setServerHits] = useState<{ ids: Set<string>; snippets: Map<string, string> } | null>(null);

  // Full-text search (including document text) runs on the server when online; metadata search always works locally.
  useEffect(() => {
    if (!online || searchTerms(query).length === 0) return setServerHits(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api<{ results: { id: string; snippet: string }[] }>(`/api/search?q=${encodeURIComponent(query)}`, { signal: ctrl.signal })
        .then((r) => setServerHits({ ids: new Set(r.results.map((x) => x.id)), snippets: new Map(r.results.map((x) => [x.id, x.snippet])) }))
        .catch(() => setServerHits(null));
    }, 250);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [query, online]);

  const items = useMemo(() => {
    if (!contracts) return [];
    const s = summarize(contracts, today);
    const all = [...s.active, ...s.expired, ...s.terminated];
    const byFilter: Record<Filter, DashboardItem<LocalContract>[]> = {
      all,
      active: s.active,
      notice: s.noticeThisMonth,
      "30": s.expiring30,
      "60": s.expiring60,
      "90": s.expiring90,
      expired: s.expired,
      terminated: s.terminated,
    };
    const list = (byFilter[filter] ?? all).filter(
      (i) => (!type || i.contract.type === type) && (!category || typeFor(i.contract.type)?.category === category || (category === "none" && !i.contract.type)),
    );
    const matched = list.filter(
      (i) =>
        matchesLocally(
          [i.contract.title, i.contract.counterparty, i.contract.notes, i.contract.tags.join(" "), extraSearchText(i.contract), ...i.contract.documents.map((d) => d.filename)],
          query,
        ) || serverHits?.ids.has(i.contract.id),
    );
    return sortContracts(matched, today, SORTS[sort] ? sort : "deadline");
  }, [contracts, today, filter, category, type, sort, query, serverHits]);
  // Only categories and types that are in use, so the filters stay short.
  const usedTypes = useMemo(() => new Set((contracts ?? []).map((c) => c.type).filter(Boolean)), [contracts]);
  const usedCategories = CATEGORIES.filter((c) => TYPES.some((t) => t.category === c.id && usedTypes.has(t.id)));
  const typesInCategory = TYPES.filter((t) => usedTypes.has(t.id) && (!category || t.category === category));

  if (!contracts) return <Spinner />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Contracts"
        actions={
          <Link to="/contracts/new" className={buttonClass()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4" aria-hidden>
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add contract
          </Link>
        }
      />
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" aria-hidden>
            <path d="m21 21-4.3-4.3M17 11a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" />
          </svg>
          <TextInput
            className="pl-9"
            type="search"
            aria-label="Search contracts"
            placeholder={online ? "Search names, people, terms, tags and document text" : "Search names, people, terms and tags"}
            value={query}
            maxLength={200}
            onChange={(e) => {
              setQuery(e.target.value);
              const next = new URLSearchParams(params);
              if (e.target.value) next.set("q", e.target.value);
              else next.delete("q");
              setParams(next, { replace: true });
            }}
          />
        </div>
        <Select aria-label="Filter" value={filter} className="sm:w-52" onChange={(e) => setParam("filter", e.target.value === "all" ? "" : e.target.value)}>
          {FILTER_ORDER.map((k) => (
            <option key={k} value={k}>
              {FILTERS[k]}
            </option>
          ))}
        </Select>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Select aria-label="Category" value={category} onChange={(e) => setParam("category", e.target.value)}>
          <option value="">All categories</option>
          {usedCategories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
          <option value="none">No type set</option>
        </Select>
        <Select aria-label="Type" value={type} onChange={(e) => setParam("type", e.target.value)} disabled={category === "none"}>
          <option value="">All types</option>
          {typesInCategory.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </Select>
        <Select aria-label="Sort by" value={sort} onChange={(e) => setParam("sort", e.target.value === "deadline" ? "" : e.target.value)}>
          {Object.entries(SORTS).map(([k, v]) => (
            <option key={k} value={k}>
              Sort: {v}
            </option>
          ))}
        </Select>
      </div>
      {items.length === 0 ? (
        <EmptyState>
          <p>{contracts.length === 0 ? "No contracts yet." : query ? `Nothing matches “${query}”.` : "No contracts in this view."}</p>
        </EmptyState>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface shadow-xs">
          {items.map((i) => (
            <li key={i.contract.id}>
              <Link to={`/contracts/${i.contract.id}`} className={cx("flex flex-wrap items-center gap-3 px-4 py-3.5 transition-colors hover:bg-subtle focus-visible:-outline-offset-2", focusRing)}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-fg sm:truncate">{i.contract.title}</div>
                  <div className="mt-0.5 truncate text-xs text-faint">
                    {[
                      typeFor(i.contract.type)?.label,
                      peopleLine(i.contract) || null,
                      contractValue(i.contract) ? formatMoney(contractValue(i.contract)!.minor, contractValue(i.contract)!.currency) : null,
                      ...i.contract.tags.map((t) => `#${t}`),
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </div>
                  {serverHits?.snippets.get(i.contract.id) && (
                    <div className="mt-1 truncate text-xs text-muted">
                      {serverHits.snippets.get(i.contract.id)!.split(/(\[\[.*?\]\])/).map((part, k) =>
                        part.startsWith("[[") ? <mark key={k}>{part.slice(2, -2)}</mark> : <span key={k}>{part}</span>,
                      )}
                    </div>
                  )}
                </div>
                {/* On phones the date and status sit under the title, so long titles aren't cut short. */}
                <div className="flex w-full items-center justify-between gap-2 text-sm tabular-nums sm:block sm:w-auto sm:text-right">
                  <div className="text-muted sm:text-fg">{listDateLine(i, today)}</div>
                  <div className="flex justify-end gap-1 sm:mt-1.5">
                    {statusBadge(i, today)}
                    {i.contract.pending && <Badge>not synced</Badge>}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
